import __var_0__ from '@stdlib/math/base/assert/is-integer';
import __var_1__ from '@stdlib/math/base/assert/is-negative-zero';
import __var_2__ from '@stdlib/constants/math/float64-pinf';
import __var_3__ from '@stdlib/constants/math/float64-ninf';
import __var_4__ from '@stdlib/constants/math/float32-smallest-subnormal'; // eslint-disable-line id-length
import __var_5__ from '@stdlib/constants/math/int8-min';
import __var_6__ from '@stdlib/constants/math/int16-min';
import __var_7__ from '@stdlib/constants/math/int32-min';
import __var_8__ from '@stdlib/constants/math/uint8-max';
import __var_9__ from '@stdlib/constants/math/uint16-max';
import __var_10__ from '@stdlib/constants/math/uint32-max';
/**

*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //
var isInteger = __var_0__;
var isNegativeZero = __var_1__;
var PINF = __var_2__;
var NINF = __var_3__;
var FLOAT32_SMALLEST_SUBNORMAL = __var_4__;
var INT8_MIN = __var_5__;
var INT16_MIN = __var_6__;
var INT32_MIN = __var_7__;
var UINT8_MAX = __var_8__;
var UINT16_MAX = __var_9__;
var UINT32_MAX = __var_10__;


// MAIN //

/**
* Returns the minimum array data type of the closest "kind" necessary for storing a provided scalar value.
*
* @param {*} value - scalar value
* @returns {string} array data type
*
* @example
* var dt = minDataType( 3.141592653589793 );
* // returns 'float32'
*
* @example
* var dt = minDataType( 3 );
* // returns 'uint8'
*/
function minDataType( value ) {
	if ( typeof value !== 'number' ) {
		return 'generic';
	}
	if ( value !== value || value === PINF || value === NINF ) {
		return 'float32';
	}
	if ( isInteger( value ) ) {
		if ( value === 0 && isNegativeZero( value ) ) {
			return 'float32';
		}
		if ( value < 0 ) {
			if ( value >= INT8_MIN ) {
				return 'int8';
			}
			if ( value >= INT16_MIN ) {
				return 'int16';
			}
			if ( value >= INT32_MIN ) {
				return 'int32';
			}
			return 'float64';
		}
		if ( value <= UINT8_MAX ) {
			return 'uint8';
		}
		if ( value <= UINT16_MAX ) {
			return 'uint16';
		}
		if ( value <= UINT32_MAX ) {
			return 'uint32';
		}
		return 'float64';
	}
	// Assume that if we are provided a tiny value, we don't want to underflow to zero by storing as `float32`...
	if (
		value > -FLOAT32_SMALLEST_SUBNORMAL &&
		value < FLOAT32_SMALLEST_SUBNORMAL
	) {
		return 'float64';
	}
	// Any number which reaches this point is less than the maximum single-precision floating-point number, as floating-point format supports a limited number of decimals (e.g., (1.0+EPS)*10**15 => 1000000000000000.2, which is less than ~3.4e38)...
	return 'float32';
}


// EXPORTS //
export default minDataType;
